/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { Text, TextInput, TouchableOpacity, View } from 'react-native';
import { MasterFilterSheetModal } from '../../shared/filters/MasterFilterSheetModal'
import { OperationalChipSelectRow } from '../../shared/filters/OperationalChipSelectRow'
import { OperationalFilterPickerRow } from '../../shared/filters/OperationalFilterPrimitives';
import { NumericTextInput } from '../../shared/ui/NumericTextInput';
import { ticketColors } from '../../shared/theme/ticketColors';
import { bulkAllCatalogWarning } from '../application/bulkPreviewScope';
import {
  BULK_SCOPE_MODE_OPTIONS,
  bulkCategoryScopeMode,
  bulkCategoryScopeModeLabel,
  type BulkCategoryScope,
  type BulkCategoryScopeMode,
} from '../application/bulkCategoryScope';
import {
  bulkPriceRoundingFromSelectId,
  bulkPriceRoundingSelectLabel,
  bulkPriceRoundingSelectOptions,
  bulkPriceRoundingToSelectId,
  bulkRoundingFamily,
  type BulkPriceRounding,
  type BulkPriceRoundingSelectId,
} from '../application/bulkPriceRounding';
import type { CategoryOption } from '../domain/categoryOptions';
import { bulkCoefficientModalStyles as styles } from './bulkCoefficientModal.styles';
import type { BulkApplyTo } from './useBulkCoefficientModal';
import { BulkCategoryMultiPicker } from './BulkCategoryMultiPicker';

type BulkCoefficientFormProps = {
  bulkCategoryScope: BulkCategoryScope;
  onBulkCategoryScopeChange: (scope: BulkCategoryScope) => void;
  bulkSearch: string;
  bulkPercentInput: string;
  bulkApplyTo: BulkApplyTo;
  scopeCounts?: { priced: number; unpriced: number } | null;
  categoryOptions: CategoryOption[];
  showApplyToSelector?: boolean;
  showBarcodeCameraButton?: boolean;
  onSearchChange: (value: string) => void;
  onOpenScanner: () => void;
  onSearchSubmitEditing?: () => void;
  onPercentChange: (value: string) => void;
  onApplyToChange: (value: BulkApplyTo) => void;
  bulkPriceRounding: BulkPriceRounding;
  onBulkPriceRoundingChange: (value: BulkPriceRounding) => void;
  moneyFractionDigits?: number;
};

export const BulkCoefficientForm = ({
  bulkCategoryScope,
  onBulkCategoryScopeChange,
  bulkSearch,
  bulkPercentInput,
  bulkApplyTo,
  scopeCounts = null,
  categoryOptions,
  showApplyToSelector = false,
  showBarcodeCameraButton = true,
  onSearchChange,
  onOpenScanner,
  onSearchSubmitEditing,
  onPercentChange,
  onApplyToChange,
  bulkPriceRounding,
  onBulkPriceRoundingChange,
  moneyFractionDigits = 2,
}: BulkCoefficientFormProps) => {
  const [scopeModePickerOpen, setScopeModePickerOpen] = useState(false);
  const [roundingPickerOpen, setRoundingPickerOpen] = useState(false);
  const scopeMode = bulkCategoryScopeMode(bulkCategoryScope);
  const roundingFamily = bulkRoundingFamily(bulkPriceRounding);
  const showPriceRounding = bulkApplyTo === 'prices';
  const showCentsFamily = moneyFractionDigits > 0;

  const scopeModeSheetOptions = useMemo(
    () => BULK_SCOPE_MODE_OPTIONS.map((o) => ({ id: o.id, name: o.label })),
    [],
  );

  const setScopeMode = (mode: BulkCategoryScopeMode) => {
    if (mode === 'all') onBulkCategoryScopeChange({ mode: 'all' });
    else if (mode === 'only') onBulkCategoryScopeChange({ mode: 'only', categoryIds: [] });
    else onBulkCategoryScopeChange({ mode: 'all_except', categoryIds: [] });
  };

  const roundingSelectOptions = useMemo(
    () => bulkPriceRoundingSelectOptions(showCentsFamily),
    [showCentsFamily],
  );
  const roundingSelectLabel = bulkPriceRoundingSelectLabel(bulkPriceRounding, roundingSelectOptions);
  const roundingSheetOptions = useMemo(
    () => roundingSelectOptions.map((o) => ({ id: o.id, name: o.label })),
    [roundingSelectOptions],
  );
  const roundingSelectId = bulkPriceRoundingToSelectId(bulkPriceRounding);

  const scopeHint = scopeCounts
    ? `${scopeCounts.priced} con precio${scopeCounts.unpriced > 0 ? ` · ${scopeCounts.unpriced} sin precio` : ''}`
    : null;

  return (
    <View style={styles.ticketFormBody}>
      <View style={styles.ticketField}>
        <OperationalFilterPickerRow
          layout="stack"
          label="Alcance"
          valueLabel={bulkCategoryScopeModeLabel(scopeMode)}
          active={scopeMode !== 'all'}
          onPress={() => setScopeModePickerOpen(true)}
          accessibilityLabel="Elegir alcance del ajuste en lote"
        />
        {scopeMode === 'only' ? (
          <BulkCategoryMultiPicker
            label="Categorías"
            pickerLabel="Elegí categorías"
            sheetTitle="Solo estas categorías"
            categoryOptions={categoryOptions}
            selectedIds={bulkCategoryScope.mode === 'only' ? bulkCategoryScope.categoryIds : []}
            onChange={(ids) => onBulkCategoryScopeChange({ mode: 'only', categoryIds: ids })}
            accessibilityLabel="Elegir categorías a incluir"
          />
        ) : null}
        {scopeMode === 'all_except' ? (
          <BulkCategoryMultiPicker
            label="Excepto"
            pickerLabel="Elegí qué no tocar"
            sheetTitle="Todo excepto"
            categoryOptions={categoryOptions}
            selectedIds={bulkCategoryScope.mode === 'all_except' ? bulkCategoryScope.categoryIds : []}
            onChange={(ids) => onBulkCategoryScopeChange({ mode: 'all_except', categoryIds: ids })}
            accessibilityLabel="Elegir categorías a excluir"
          />
        ) : null}
        {scopeHint ? (
          <Text style={styles.ticketHint} accessibilityLiveRegion="polite">
            {scopeHint}
          </Text>
        ) : null}
      </View>

      <View style={styles.ticketField}>
        <Text style={styles.ticketFieldLabel}>Filtrar productos (opcional)</Text>
        <View style={styles.ticketInpRow}>
          <TextInput
            style={[styles.ticketInp, styles.ticketInpFlex]}
            value={bulkSearch}
            onChangeText={onSearchChange}
            placeholder="Nombre, SKU, código…"
            placeholderTextColor={ticketColors.ink3}
            onSubmitEditing={onSearchSubmitEditing}
            accessibilityLabel="Filtrar productos para coeficiente en lote"
          />
          {showBarcodeCameraButton ? (
            <TouchableOpacity
              style={styles.ticketScanBtn}
              onPress={onOpenScanner}
              accessibilityRole="button"
              accessibilityLabel="Escanear"
            >
              <Text style={styles.ticketScanGlyph}>▥</Text>
            </TouchableOpacity>
          ) : null}
        </View>
        {scopeMode === 'all' ? <Text style={styles.ticketWarn}>{bulkAllCatalogWarning}</Text> : null}
      </View>

      <View style={styles.ticketField}>
        <Text style={styles.ticketFieldLabel}>Porcentaje</Text>
        <NumericTextInput
          kind="percent"
          style={styles.ticketInp}
          value={bulkPercentInput}
          onChangeText={onPercentChange}
          placeholder="10 o -15"
          placeholderTextColor={ticketColors.ink3}
          accessibilityLabel="Porcentaje de ajuste en lote"
        />
      </View>

      {showApplyToSelector ? (
        <View style={styles.ticketField}>
          <Text style={styles.ticketFieldLabel}>Aplicar a</Text>
          <OperationalChipSelectRow
            ticketTone
            value={bulkApplyTo}
            onChange={onApplyToChange}
            options={[
              { value: 'prices', label: 'Precios' },
              { value: 'quantities', label: 'Cantidades de esta sucursal' },
            ]}
          />
        </View>
      ) : null}

      {showPriceRounding ? (
        <View style={styles.ticketField}>
          <OperationalFilterPickerRow
            layout="stack"
            label="Redondeo"
            valueLabel={roundingSelectLabel}
            active={roundingFamily !== 'none'}
            onPress={() => setRoundingPickerOpen(true)}
            accessibilityLabel="Elegir redondeo de precios"
          />
        </View>
      ) : null}

      <MasterFilterSheetModal
        visible={scopeModePickerOpen}
        title="Alcance"
        options={scopeModeSheetOptions}
        selectedId={scopeMode}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) setScopeMode(id as BulkCategoryScopeMode);
          setScopeModePickerOpen(false);
        }}
        onClose={() => setScopeModePickerOpen(false)}
      />

      <MasterFilterSheetModal
        visible={roundingPickerOpen}
        title="Redondeo"
        options={roundingSheetOptions}
        selectedId={roundingSelectId}
        showAllOption={false}
        appearance="ticket"
        onSelect={(id) => {
          if (id) {
            onBulkPriceRoundingChange(bulkPriceRoundingFromSelectId(id as BulkPriceRoundingSelectId));
          }
        }}
        onClose={() => setRoundingPickerOpen(false)}
      />
    </View>
  );
};
